import React from 'react';
import { PRINT_ADS, DIGITAL_ADS } from '../config';
import { getSpecialEdition, getOnlyIssue, getAdDue } from '../utils/pricing';

// Shared by the paid confirmation and the invoice-requested page, so both tell
// the advertiser the same deadline, specs and way to send their ad.

// 'Wed, Oct 14'
export function fmtDay(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// 'Hope Food Pantry, Inc.' -> 'Hope-Food-Pantry-Inc', for suggested file names.
export function fileSlug(name) {
  const slug = String(name || '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 24).replace(/-+$/, '');
  return slug || 'YourOrg';
}

// What they bought, as spec lines: print (with orientation) then digital.
function placements(form) {
  const hasPrint   = form.medium === 'print'   || form.medium === 'both';
  const hasDigital = form.medium === 'digital' || form.medium === 'both';
  const out = [];
  const printAd = hasPrint && form.printAd ? PRINT_ADS.find(a => a.id === form.printAd) : null;
  if (printAd) {
    const multi = printAd.specs.length > 1;
    const spec  = multi
      ? (printAd.specs.find(s => s.orientation === form.orientation) || printAd.specs[0])
      : printAd.specs[0];
    out.push({
      key: 'print', chip: 'Print', print: true,
      name: `${printAd.label}${multi ? ` (${spec.label})` : ''}`,
      dims: spec.dims, detail: printAd.format,
      files: printAd.feature && printAd.feature.files,
      notes: printAd.feature && printAd.feature.sendNotes,
    });
  }
  if (hasDigital) {
    (form.digitalAds || []).map(id => DIGITAL_ADS.find(a => a.id === id)).filter(Boolean).forEach(ad => {
      out.push({
        key: ad.id, chip: ad.manifestLabel.startsWith('EMAIL') ? 'Email' : 'Web',
        name: ad.label, dims: ad.dimensions,
        detail: 'RGB PNG or JPEG · plus the web address it should link to',
      });
    });
  }
  return out;
}

// The "Send us your ad" card: first deadline, what to send, how to send it.
export function SendAdCard({ form, runSchedule, eyebrow = 'Next step', className = '' }) {
  const firstDate = runSchedule && runSchedule[0];
  const firstDue  = firstDate ? getAdDue(firstDate) : null;
  const items     = placements(form);
  const print     = items.find(p => p.print);

  // A one-issue special has a single deadline for a single, named issue.
  const onlyIssue = form.printAd && (form.medium === 'print' || form.medium === 'both')
    ? getOnlyIssue(form.printAd)
    : null;
  const edition   = onlyIssue ? getSpecialEdition(onlyIssue) : null;

  // Issues in this run whose ad files are due earlier than the usual Monday.
  const earlyIssues = (runSchedule || []).filter(d => {
    const se = getSpecialEdition(d);
    return se && se.adDueDaysBefore;
  });

  // "Email your ad files" opens their mail app already addressed, with the
  // subject the ad desk files creative under and the run dates in the body.
  const subject = `${form.contact.businessName} Ad Creative`;
  const body = [
    'Attached are our ad files.',
    '',
    `Business: ${form.contact.businessName}`,
    print ? `Ad: ${print.name}` : null,
    `Run: ${(runSchedule || []).map(d => fmtDay(d)).join(', ')}`,
  ].filter(line => line !== null).join('\n');
  const mailto = `mailto:ads@hendersonvillelightning.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <div className={`send-card ${className}`.trim()}>
      <div className="send-eyebrow">{eyebrow}</div>
      <div className="send-title">Send us your ad</div>

      {firstDue && (
        <div className="send-deadline">
          <div className="send-cal" aria-hidden="true">
            <div className="send-cal-month">{firstDue.toLocaleDateString('en-US', { month: 'short' })}</div>
            <div className="send-cal-day">{firstDue.getDate()}</div>
          </div>
          <div>
            <div className="send-deadline-lead">{onlyIssue ? 'Your ad is due' : 'Your first ad is due'}</div>
            <div className="send-deadline-date">
              {firstDue.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              <span className="send-comma">, </span>
              <span className="send-eod">by end of day</span>
            </div>
            <div className="send-deadline-sub">
              {onlyIssue
                ? `For the ${edition ? `${edition.name} issue` : 'issue'}, ${fmtDay(onlyIssue)}.`
                : earlyIssues.length
                  ? `Ads are due by end of day the Monday before each Wednesday issue, except ${earlyIssues.map(d => `${getSpecialEdition(d).name}: ${fmtDay(getAdDue(d))}`).join(', ')}.`
                  : 'Ads are always due by end of day the Monday before each Wednesday issue.'}
            </div>
          </div>
        </div>
      )}

      <div className="send-heading">What to send</div>
      <ul className="send-specs">
        {items.map(p => (
          <li key={p.key}>
            <span className={`send-chip${p.print ? ' send-chip-print' : ''}`}>{p.chip}</span>
            <div className="send-spec-body">
              <div className="send-spec-name">{p.name}</div>
              <div className="send-spec-detail"><b>{p.dims}</b> &middot;&nbsp;{p.detail}</div>
              {p.files && (
                <div className="send-spec-files">
                  Name them {p.files.join(' and ')}:
                  {p.files.map(f => <code key={f}>{fileSlug(form.contact.businessName)}-{f}.pdf</code>)}
                </div>
              )}
              {p.notes && p.notes.map(n => <div key={n} className="send-spec-note">{n}</div>)}
            </div>
          </li>
        ))}
      </ul>

      <div className="send-heading">How to send it</div>
      <div className="send-howto">
        <a className="send-btn" href={mailto}>Email your ad files</a>
        <div className="send-subject">
          to ads@hendersonvillelightning.com<br />
          Subject: <code>{subject}</code>
        </div>
      </div>
      {runSchedule && runSchedule.length > 1 && (
        <p className="send-fine">
          Running the same ad every week? Send it once. Changing it for a later issue? Send the new version by that issue&rsquo;s due date.
        </p>
      )}
    </div>
  );
}

// Issue dates with each issue's ad deadline.
export function RunScheduleTable({ runSchedule }) {
  return (
    <table className="send-schedule">
      <thead>
        <tr><th>Issue</th><th>Ad due</th></tr>
      </thead>
      <tbody>
        {(runSchedule || []).map((d, i) => {
          const se = getSpecialEdition(d);
          return (
            <tr key={i}>
              <td>
                {fmtDay(d)}
                {se && <span className="se-label">{se.name}</span>}
              </td>
              <td className="send-due">
                {fmtDay(getAdDue(d))}
                {i > 0 && <span>only if changing your ad</span>}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
